import { supabase } from '../supabase';
import type { Database } from '@/types/database';

export type Profile = Database['public']['Tables']['profiles']['Row'];
export type ProfileUpdate = Database['public']['Tables']['profiles']['Update'];

/**
 * Update profile settings (business profile, fiscal, invoice, product defaults, etc.)
 */
export async function updateSettings(
  userId: string,
  updates: ProfileUpdate
): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Failed to update settings:', error.message);
    throw new Error(`Failed to update settings: ${error.message}`);
  }

  return data;
}

/**
 * Upload business logo to Supabase Storage
 */
export async function uploadLogo(
  userId: string,
  file: File
): Promise<string> {
  const fileExt = file.name.split('.').pop();
  const filePath = `${userId}/logo.${fileExt}`;

  // Remove existing logo first
  await supabase.storage.from('logos').remove([`${userId}/logo.jpg`, `${userId}/logo.png`, `${userId}/logo.jpeg`, `${userId}/logo.webp`]);

  const { error: uploadError } = await supabase.storage
    .from('logos')
    .upload(filePath, file, { upsert: true });

  if (uploadError) {
    console.error('Failed to upload logo:', uploadError.message);
    throw new Error(`Failed to upload logo: ${uploadError.message}`);
  }

  // Generate a private signed URL with 10-year expiration
  // (315360000 seconds = 10 years)
  const { data: signedUrlData, error: urlError } = await supabase.storage
    .from('logos')
    .createSignedUrl(filePath, 315360000);

  if (urlError) {
    console.error('Failed to create signed URL:', urlError.message);
    throw new Error(`Failed to create signed URL: ${urlError.message}`);
  }

  // Update profile with signed logo URL
  const { error: updateError } = await supabase
    .from('profiles')
    .update({ logo_url: signedUrlData.signedUrl })
    .eq('id', userId);

  if (updateError) {
    console.error('Failed to update logo URL:', updateError.message);
    throw new Error(`Failed to update logo URL: ${updateError.message}`);
  }

  return signedUrlData.signedUrl;
}

/**
 * Remove business logo
 */
export async function removeLogo(userId: string): Promise<void> {
  // Remove file from storage
  await supabase.storage.from('logos').remove([
    `${userId}/logo.jpg`,
    `${userId}/logo.png`,
    `${userId}/logo.jpeg`,
    `${userId}/logo.webp`,
  ]);

  // Clear logo URL in profile
  const { error } = await supabase
    .from('profiles')
    .update({ logo_url: null })
    .eq('id', userId);

  if (error) {
    console.error('Failed to remove logo URL:', error.message);
    throw new Error(`Failed to remove logo URL: ${error.message}`);
  }
}

/**
 * Change login email (auth.users) — sends confirmation to new address
 */
export async function changeLoginEmail(newEmail: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({
    email: newEmail,
  }, { emailRedirectTo: `${window.location.origin}/settings?tab=personal` });

  if (error) {
    console.error('Failed to change login email:', error.message);
    throw new Error(`Failed to change login email: ${error.message}`);
  }
}

/**
 * Change user password
 */
export async function changePassword(newPassword: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  if (error) {
    console.error('Failed to change password:', error.message);
    throw new Error(`Failed to change password: ${error.message}`);
  }
}

/**
 * Sign out from all devices
 */
export async function signOutAll(): Promise<void> {
  const { error } = await supabase.auth.signOut({ scope: 'global' });

  if (error) {
    console.error('Failed to sign out all:', error.message);
    throw new Error(`Failed to sign out all: ${error.message}`);
  }
}

/**
 * Export all user data (GDPR compliance)
 */
export async function exportUserData(userId: string): Promise<Record<string, unknown>> {
  const tables = ['clients', 'suppliers', 'products', 'invoices', 'expenses'] as const;
  const exportData: Record<string, unknown> = {};

  // Fetch profile
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (profileError) throw new Error(`Export failed: ${profileError.message}`);
  exportData.profile = profile;

  // Fetch each table's data
  for (const table of tables) {
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .eq('user_id', userId);

    if (error) {
      console.error(`Failed to export ${table}:`, error.message);
      exportData[table] = { error: error.message };
    } else {
      exportData[table] = data;
    }
  }

  // Fetch expense categories (custom ones)
  const { data: categories, error: catError } = await supabase
    .from('expense_categories')
    .select('*')
    .eq('user_id', userId);

  if (!catError) {
    exportData.expense_categories = categories;
  }

  exportData.exported_at = new Date().toISOString();
  return exportData;
}

/**
 * Delete user account — GDPR hard delete via Edge Function.
 * Removes all storage files, cascades DB rows, and deletes the auth.users record.
 */
export async function deleteAccount(_userId: string): Promise<void> {
  // Retrieve session to forward the JWT explicitly — required by the Edge Function.
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No active session');

  const { data, error } = await supabase.functions.invoke('delete-account', {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });

  if (error) {
    console.error('Failed to delete account:', error.message);
    throw new Error(error.message);
  }

  if (!data?.success) {
    throw new Error(data?.error ?? 'Account deletion failed');
  }

  // Clear local session (auth user is already deleted server-side)
  await supabase.auth.signOut({ scope: 'local' });
}
